import React, { FunctionComponent, useContext } from "react";
import { Button, CloseIconThin } from "@cmsgov/ds-medicare-gov";
import {
  DrugCoverageFilterOption,
  PlanSNPType,
  PlanType,
} from "../../../@types";
import { AppContext } from "../../../app/store";
import {
  doFiltersExist,
  isMedicareAdvantageType,
  useTranslate,
} from "../../../helpers";
import { updateFilter, updateSNPFilters } from "./helpers";
import { default as C } from "./constants";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
  Ga4Event,
} from "../../../app/contexts/Analytics/types";

interface FilterChipsProps {
  pageReset: () => void;
}

const FilterChips: FunctionComponent<FilterChipsProps> = ({ pageReset }) => {
  const t = useTranslate();
  const context = useContext(AppContext);
  const {
    state: { searchResultsFilters, snpFilters, planType },
    dispatch,
  } = context;
  const isMA = isMedicareAdvantageType(planType || PlanType.MAPD);

  const convertFilterKeysToLabels = (filters: string[]) =>
    filters
      ? filters.reduce<{ key: string; label: string }[]>(
          (acc, filter) => [
            ...acc,
            {
              key: filter,
              label: t(C.filterLabelsByValue[filter]),
            },
          ],
          []
        )
      : [];

  const filterLabels = convertFilterKeysToLabels(
    searchResultsFilters.planCoveragesFilter
  );

  const handleFilterRemove = (filter: string): void => {
    dispatch({
      type: AnalyticsActionType.SEND_GA4_EVENT,
      settings: {
        event_name: Ga4Event.FILTER_CLEARED,
        text: filter,
      },
    });

    updateFilter(
      context,
      C.SearchFilterKeys.planCoveragesFilter,
      searchResultsFilters.planCoveragesFilter.filter(f => f !== filter)
    );
    pageReset();
  };

  const filtersExist =
    doFiltersExist(searchResultsFilters) || (snpFilters?.length && isMA);

  return filtersExist ? (
    <ul
      className="FilterChips mct-c-filter-chips ds-c-list--bare ds-u-display--flex ds-u-flex-wrap--wrap"
      role="list"
    >
      {filterLabels.map(filter => (
        <li
          key={filter.key}
          role="listitem"
          className="mct-c-filter-chips__item"
        >
          <Button
            className="mct-c-filter-chips__button"
            size="small"
            onClick={(): void => {
              dispatch({
                type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                settings: {
                  button: {
                    buttonStyle: AnalyticsButtonStyle.DEFAULT,
                    buttonType: AnalyticsButtonType.BUTTON,
                    text: filter.label,
                  },
                },
              });
              handleFilterRemove.call(null, filter.key);
            }}
            data-testid={filter.key}
          >
            <span className="ds-u-visibility--screen-reader">
              {t("filter_labels.applied_aria")}
            </span>
            <span className="mct-c-filter-chips__label">{filter.label}</span>
            <CloseIconThin className="mct-c-filter-chips__button-icon" />
            <span className="ds-u-visibility--screen-reader">
              {t("filter_labels.aria")}
            </span>
          </Button>
        </li>
      ))}

      {searchResultsFilters.insuranceCarrierFilter && (
        <li
          key={searchResultsFilters.insuranceCarrierFilter}
          className="mct-c-filter-chips__item"
        >
          <Button
            className="mct-c-filter-chips__button"
            size="small"
            onClick={(): void => {
              dispatch({
                type: AnalyticsActionType.SEND_GA4_EVENT,
                settings: {
                  event_name: Ga4Event.FILTER_CLEARED,
                  text: searchResultsFilters.insuranceCarrierFilter,
                },
              });
              dispatch({
                type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                settings: {
                  button: {
                    buttonStyle: AnalyticsButtonStyle.DEFAULT,
                    buttonType: AnalyticsButtonType.BUTTON,
                    text: searchResultsFilters.insuranceCarrierFilter,
                  },
                },
              });
              updateFilter(
                context,
                C.SearchFilterKeys.insuranceCarrierFilter,
                ""
              );
              pageReset();
            }}
            data-testid={searchResultsFilters.insuranceCarrierFilter}
          >
            <span className="ds-u-visibility--screen-reader">
              {t("filter_labels.applied_aria")}
            </span>
            <span className="mct-c-filter-chips__label">
              {searchResultsFilters.insuranceCarrierFilter}
            </span>
            <CloseIconThin className="mct-c-filter-chips__button-icon" />
            <span className="ds-u-visibility--screen-reader">
              {t("filter_labels.aria")}
            </span>
          </Button>
        </li>
      )}

      {!!searchResultsFilters.starRatingFilter.length && (
        <li
          key={searchResultsFilters.starRatingFilter}
          className="mct-c-filter-chips__item"
        >
          <Button
            className="mct-c-filter-chips__button"
            size="small"
            onClick={(): void => {
              const eventText = `${searchResultsFilters.starRatingFilter} ${
                Number(searchResultsFilters.starRatingFilter) === 5
                  ? t("plan_terms.stars")
                  : t("plan_terms.stars_and_up")
              }`;

              dispatch({
                type: AnalyticsActionType.SEND_GA4_EVENT,
                settings: {
                  event_name: Ga4Event.FILTER_CLEARED,
                  text: eventText,
                },
              });
              dispatch({
                type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                settings: {
                  button: {
                    buttonStyle: AnalyticsButtonStyle.DEFAULT,
                    buttonType: AnalyticsButtonType.BUTTON,
                    text: eventText,
                  },
                },
              });
              updateFilter(context, C.SearchFilterKeys.starRatingFilter, "");
              pageReset();
            }}
            data-testid={searchResultsFilters.starRatingFilter}
          >
            <span className="ds-u-visibility--screen-reader">
              {t("filter_labels.applied_aria")}
            </span>
            <span className="mct-c-filter-chips__label">
              {searchResultsFilters.starRatingFilter}{" "}
              {Number(searchResultsFilters.starRatingFilter) === 5
                ? t("plan_terms.stars")
                : t("plan_terms.stars_and_up")}{" "}
            </span>
            <CloseIconThin className="mct-c-filter-chips__button-icon" />
            <span className="ds-u-visibility--screen-reader">
              {t("filter_labels.aria")}
            </span>
          </Button>
        </li>
      )}

      {searchResultsFilters.drugCoverageFilter && (
        <li
          key={searchResultsFilters.drugCoverageFilter}
          className="mct-c-filter-chips__item"
        >
          <Button
            className="mct-c-filter-chips__button"
            size="small"
            onClick={(): void => {
              const eventText =
                searchResultsFilters.drugCoverageFilter ===
                DrugCoverageFilterOption.INCLUDES
                  ? t("search_results_page.includes_drug_coverage")
                  : t("search_results_page.excludes_drug_coverage");

              dispatch({
                type: AnalyticsActionType.SEND_GA4_EVENT,
                settings: {
                  event_name: Ga4Event.FILTER_CLEARED,
                  text: eventText,
                },
              });
              dispatch({
                type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                settings: {
                  button: {
                    buttonStyle: AnalyticsButtonStyle.DEFAULT,
                    buttonType: AnalyticsButtonType.BUTTON,
                    text: eventText,
                  },
                },
              });

              updateFilter(
                context,
                C.SearchFilterKeys.drugCoverageFilter,
                undefined
              );
              pageReset();
            }}
            data-testid={searchResultsFilters.drugCoverageFilter}
          >
            <span className="ds-u-visibility--screen-reader">
              {t("filter_labels.applied_aria")}
            </span>
            <span className="mct-c-filter-chips__label">
              {searchResultsFilters.drugCoverageFilter ===
              DrugCoverageFilterOption.INCLUDES
                ? t("search_results_page.includes_drug_coverage")
                : t("search_results_page.excludes_drug_coverage")}
            </span>
            <CloseIconThin className="mct-c-filter-chips__button-icon" />
            <span className="ds-u-visibility--screen-reader">
              {t("filter_labels.aria")}
            </span>
          </Button>
        </li>
      )}

      {searchResultsFilters.planCategoriesFilter.map(cat => {
        const categoryName = t(C.categoryFilterTranslationMap[cat]);
        return (
          <li key={cat} className="mct-c-filter-chips__item">
            <Button
              className="mct-c-filter-chips__button"
              size="small"
              onClick={(): void => {
                const selectedFilters = searchResultsFilters.planCategoriesFilter.filter(
                  f => f !== cat
                );

                dispatch({
                  type: AnalyticsActionType.SEND_GA4_EVENT,
                  settings: {
                    event_name: Ga4Event.FILTER_CLEARED,
                    text: categoryName,
                  },
                });

                dispatch({
                  type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                  settings: {
                    button: {
                      buttonStyle: AnalyticsButtonStyle.DEFAULT,
                      buttonType: AnalyticsButtonType.BUTTON,
                      text: categoryName,
                    },
                  },
                });

                updateFilter(
                  context,
                  C.SearchFilterKeys.planCategoriesFilter,
                  selectedFilters
                );
                pageReset();
              }}
              data-testid={cat}
            >
              <span className="ds-u-visibility--screen-reader">
                {t("filter_labels.applied_aria")}
              </span>
              <span className="mct-c-filter-chips__label">{categoryName}</span>
              <CloseIconThin className="mct-c-filter-chips__button-icon" />
              <span className="ds-u-visibility--screen-reader">
                {t("filter_labels.aria")}
              </span>
            </Button>
          </li>
        );
      })}

      {isMA &&
        snpFilters?.map(filter => {
          if (filter !== PlanSNPType.SNP_TYPE_NOT_SNP) {
            const filterName = t(C.snpFilterTranslationMap[filter]);

            return (
              <li key={filter} className="mct-c-filter-chips__item">
                <Button
                  className="mct-c-filter-chips__button"
                  size="small"
                  onClick={(): void => {
                    const selectedFilters = snpFilters.filter(
                      f => f !== filter
                    );

                    dispatch({
                      type: AnalyticsActionType.SEND_GA4_EVENT,
                      settings: {
                        event_name: Ga4Event.FILTER_CLEARED,
                        text: filterName,
                      },
                    });
                    dispatch({
                      type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                      settings: {
                        button: {
                          buttonStyle: AnalyticsButtonStyle.DEFAULT,
                          buttonType: AnalyticsButtonType.BUTTON,
                          text: filterName,
                        },
                      },
                    });
                    updateSNPFilters(context, selectedFilters);
                    pageReset();
                  }}
                  data-testid={filter}
                >
                  <span className="ds-u-visibility--screen-reader">
                    {t("filter_labels.applied_aria")}
                  </span>
                  <span className="mct-c-filter-chips__label">
                    {filterName}
                  </span>
                  <CloseIconThin className="mct-c-filter-chips__button-icon" />
                  <span className="ds-u-visibility--screen-reader">
                    {t("filter_labels.aria")}
                  </span>
                </Button>
              </li>
            );
          }
          return null;
        })}
    </ul>
  ) : null;
};

export default FilterChips;
